/**
 * PAGE: Confirm Match
 *
 * RESPONSIBILITY:
 * - Show verification result
 * - Allow proceed or go back
 *
 * MUST NEVER:
 * - Initialise Zustand
 * - Save balls
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { verifyMatchReady, initialiseSession } from "../../api/ballbyball.api";

export default function ConfirmMatch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Extract the verify call so the "Re-check" button can re-fetch
  // without a full window.location.reload (which the prior version
  // used — a harsh reset that also lost any in-page banner state).
  const fetchCheck = useCallback(() => {
    setLoading(true);
    setError(null);
    return verifyMatchReady(id)
      .then((r) => { setData(r.data); })
      .catch((err) => {
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Unable to verify match."
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchCheck(); }, [fetchCheck]);

  const team1Players = useMemo(() => {
    if (!data?.team1_players) return [];
    return data.team1_players.map((p) => ({
      id: p.player_id,
      // The Django stats endpoint joins through to player__nick_name when
      // available — prefer that so we render the operator-set display
      // name; fall back to first+last only if the join row didn't include
      // a nick_name.
      name: (p.player__nick_name || "").trim()
        || `${p.player__first_name || ""} ${p.player__last_name || ""}`.trim(),
    }));
  }, [data]);

  const team2Players = useMemo(() => {
    if (!data?.team2_players) return [];
    return data.team2_players.map((p) => ({
      id: p.player_id,
      // The Django stats endpoint joins through to player__nick_name when
      // available — prefer that so we render the operator-set display
      // name; fall back to first+last only if the join row didn't include
      // a nick_name.
      name: (p.player__nick_name || "").trim()
        || `${p.player__first_name || ""} ${p.player__last_name || ""}`.trim(),
    }));
  }, [data]);

  const xiMap = data?.playing_xi || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="bg-bg-primary rounded-2xl p-6 border border-bg-secondary">
          <p className="text-text-secondary">Checking match…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center p-4">
        <div className="bg-bg-primary rounded-2xl p-6 border border-red-500/30 max-w-lg w-full text-center">
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Unable to Proceed
          </h2>
          <p className="text-text-secondary mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-bg-secondary text-text-primary rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data?.allowed) {
    // Best-effort reason inspection so the right step lights up. Backend
    // free-text reasons are matched against common phrases. When we
    // can't pattern-match we still surface the raw text below so the
    // operator sees the server's exact complaint.
    const reasonStr = (data?.reason || "").toString().toLowerCase();
    const tossMissing = !reasonStr || /toss/.test(reasonStr);
    const xiMissing = !reasonStr || /(xi|playing|squad|11|eleven)/.test(reasonStr);
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center p-4">
        <div className="bg-bg-primary rounded-2xl p-6 border border-amber-500/30 max-w-xl w-full">
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Match prep incomplete
          </h2>
          <p className="text-text-secondary mb-3">
            {data?.reason || "This match is not ready for scoring yet. Finish the prep steps below."}
          </p>
          <ul className="text-[12px] text-text-secondary mb-5 list-disc list-inside space-y-0.5">
            {tossMissing && <li><span className="text-amber-300 font-semibold">Toss</span> not set yet.</li>}
            {xiMissing && <li><span className="text-blue-300 font-semibold">Playing XI</span> incomplete or unsaved (need 11 + captain + keeper for both sides).</li>}
            {!tossMissing && !xiMissing && (
              <li>See the server message above for the exact step still pending.</li>
            )}
          </ul>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
            <button
              onClick={() => navigate(`/cricket/matches/${id}/toss`)}
              className={`bg-bg-main hover:bg-amber-500/10 border rounded-lg p-3 text-left transition-colors ${
                tossMissing ? "border-amber-400 ring-1 ring-amber-400/40" : "border-amber-400/30"
              }`}
            >
              <div className="text-xs uppercase tracking-wider text-amber-400 mb-1">Step 1</div>
              <div className="text-sm font-semibold text-text-primary">Set toss</div>
              <div className="text-xs text-text-secondary mt-0.5">Who won, what they chose</div>
            </button>
            <button
              onClick={() => navigate(`/cricket/matches/${id}/playing-xi`)}
              className={`bg-bg-main hover:bg-blue-500/10 border rounded-lg p-3 text-left transition-colors ${
                xiMissing ? "border-blue-400 ring-1 ring-blue-400/40" : "border-blue-400/30"
              }`}
            >
              <div className="text-xs uppercase tracking-wider text-blue-400 mb-1">Step 2</div>
              <div className="text-sm font-semibold text-text-primary">Pick Playing XI</div>
              <div className="text-xs text-text-secondary mt-0.5">11 per side · captain · keeper</div>
            </button>
            <button
              onClick={() => navigate(`/cricket/matches/${id}/edit`)}
              className="bg-bg-main hover:bg-bg-secondary/60 border border-border-default rounded-lg p-3 text-left transition-colors"
            >
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1">Adjust</div>
              <div className="text-sm font-semibold text-text-primary">Edit match details</div>
              <div className="text-xs text-text-secondary mt-0.5">Date, venue, format</div>
            </button>
          </div>
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate("/cricket/matches")}
              className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary"
            >
              ← Back to match list
            </button>
            <button
              onClick={() => fetchCheck()}
              className="px-3 py-1.5 text-xs bg-bg-secondary hover:bg-bg-main rounded text-text-primary"
              title="Re-check without a full page reload"
            >
              Re-check readiness
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main p-6">
      <div className="max-w-5xl mx-auto bg-bg-primary rounded-2xl p-6 border border-bg-secondary">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-text-primary">
            Confirm Match
          </h1>
          <p className="text-text-secondary">
            {data.match_title || "Match"} · {data.match_date || ""}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-bg-main rounded-xl p-4 border border-bg-secondary">
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              {data.team1_name || "Team 1"} Playing XI
            </h2>
            <ol className="list-decimal list-inside text-text-secondary space-y-1">
              {xiMap?.[data.team1]?.map((p) => {
                const name =
                  team1Players.find((t) => t.id === p.player_id)?.name ||
                  p.player_id;
                return (
                  <li key={p.player_id} className="text-sm">
                    {name}
                    {p.is_captain ? " (C)" : ""}
                    {p.is_wicket_keeper ? " (WK)" : ""}
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="bg-bg-main rounded-xl p-4 border border-bg-secondary">
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              {data.team2_name || "Team 2"} Playing XI
            </h2>
            <ol className="list-decimal list-inside text-text-secondary space-y-1">
              {xiMap?.[data.team2]?.map((p) => {
                const name =
                  team2Players.find((t) => t.id === p.player_id)?.name ||
                  p.player_id;
                return (
                  <li key={p.player_id} className="text-sm">
                    {name}
                    {p.is_captain ? " (C)" : ""}
                    {p.is_wicket_keeper ? " (WK)" : ""}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-bg-secondary text-text-primary rounded-lg"
          >
            Go Back
          </button>
          <button
            data-testid="confirm-match-proceed"
            onClick={async () => {
              await initialiseSession(id);
              navigate(`/ball/scoring/${id}`);
            }}
            className="px-4 py-2 bg-button-primary text-text-primary rounded-lg"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}
